/**
 * Loading System Feature Flags
 * Controls which parts of the new loading system are enabled
 */

export interface LoadingFeatureFlags {
  // Core loading system
  NEW_LOADING_SYSTEM: boolean;
  
  // Debug and development features
  LOADING_DEBUG_PANEL: boolean;
  LOADING_DEBUG_LOGS: boolean;
  
  // Idle detection and management
  IDLE_DETECTION: boolean;
  IDLE_WARNING: boolean;
  
  // Performance monitoring
  PERFORMANCE_TRACKING: boolean;
  LOADING_METRICS: boolean;
  
  // Smart loading components
  SMART_LOADING_INDICATORS: boolean;
  SKELETON_LOADERS: boolean;
  
  // Router integration
  ROUTER_LOADING_INTEGRATION: boolean;
  DATA_LOADER_ENHANCEMENT: boolean;
}

// Environment-based feature configuration
export const LOADING_FEATURES: LoadingFeatureFlags = {
  // Core system - default ON in development for router integration
  NEW_LOADING_SYSTEM: import.meta.env.DEV || import.meta.env.VITE_NEW_LOADING_SYSTEM === 'true',
  
  // Debug features - ON in development, OFF in production
  LOADING_DEBUG_PANEL: import.meta.env.DEV || import.meta.env.VITE_LOADING_DEBUG === 'true',
  LOADING_DEBUG_LOGS: import.meta.env.DEV || import.meta.env.VITE_LOADING_DEBUG_LOGS === 'true',
  
  // Idle detection - default ON (safe feature)
  IDLE_DETECTION: import.meta.env.VITE_IDLE_DETECTION !== 'false',
  IDLE_WARNING: import.meta.env.VITE_IDLE_WARNING !== 'false',
  
  // Performance tracking - ON in development
  PERFORMANCE_TRACKING: import.meta.env.DEV || import.meta.env.VITE_LOADING_METRICS === 'true',
  LOADING_METRICS: import.meta.env.DEV || import.meta.env.VITE_LOADING_METRICS === 'true',
  
  // Smart components - follow main system flag
  SMART_LOADING_INDICATORS: import.meta.env.VITE_NEW_LOADING_SYSTEM === 'true',
  SKELETON_LOADERS: import.meta.env.VITE_SKELETON_LOADERS === 'true',
  
  // Router integration - most critical, default OFF in production, ON in development for testing
  ROUTER_LOADING_INTEGRATION: import.meta.env.DEV || import.meta.env.VITE_ROUTER_LOADING_INTEGRATION === 'true',
  DATA_LOADER_ENHANCEMENT: import.meta.env.VITE_DATA_LOADER_ENHANCEMENT === 'true'
};

// Utility functions for feature checking
export const isLoadingSystemEnabled = () => LOADING_FEATURES.NEW_LOADING_SYSTEM;
export const isDebugPanelEnabled = () => LOADING_FEATURES.LOADING_DEBUG_PANEL;
export const isIdleDetectionEnabled = () => LOADING_FEATURES.IDLE_DETECTION;
export const isRouterIntegrationEnabled = () => LOADING_FEATURES.ROUTER_LOADING_INTEGRATION;

// Feature validation and warnings
export const validateLoadingFeatures = (): void => {
  const features = LOADING_FEATURES;
  
  // NOTE: Reduced logging since we migrated to new project context system
  // Only log if explicitly requested via environment variable
  if (import.meta.env.VITE_LOADING_DEBUG_VERBOSE === 'true') {
    console.log('🔧 [LoadingFeatures] Feature flags configuration:');
    console.table(features);
  }
  
  // Validate feature combinations
  if (features.ROUTER_LOADING_INTEGRATION && !features.NEW_LOADING_SYSTEM) {
    console.warn('⚠️ [LoadingFeatures] Router integration enabled but main system disabled');
  }
  
  if (features.SMART_LOADING_INDICATORS && !features.NEW_LOADING_SYSTEM) {
    console.warn('⚠️ [LoadingFeatures] Smart indicators enabled but main system disabled');
  }
  
  // Development mode warnings
  if (import.meta.env.DEV) {
    if (!features.LOADING_DEBUG_PANEL) {
      console.info('ℹ️ [LoadingFeatures] Debug panel disabled in development');
    }
    
    if (!features.LOADING_DEBUG_LOGS) {
      console.info('ℹ️ [LoadingFeatures] Debug logs disabled in development');
    }
  }
  
  // Production mode warnings
  if (import.meta.env.PROD) {
    if (features.LOADING_DEBUG_PANEL) {
      console.warn('⚠️ [LoadingFeatures] Debug panel enabled in production');
    }
    
    if (features.LOADING_DEBUG_LOGS) {
      console.warn('⚠️ [LoadingFeatures] Debug logs enabled in production');
    }
  }
};

// Safe feature enablement helper
export const enableLoadingFeatureSafely = (featureName: keyof LoadingFeatureFlags): boolean => {
  const currentValue = LOADING_FEATURES[featureName];
  
  if (currentValue) {
    console.log(`✅ [LoadingFeatures] ${featureName} is already enabled`);
    return true;
  }
  
  // Check dependencies
  const dependencies: Partial<Record<keyof LoadingFeatureFlags, (keyof LoadingFeatureFlags)[]>> = {
    SMART_LOADING_INDICATORS: ['NEW_LOADING_SYSTEM'],
    ROUTER_LOADING_INTEGRATION: ['NEW_LOADING_SYSTEM'],
    DATA_LOADER_ENHANCEMENT: ['NEW_LOADING_SYSTEM', 'ROUTER_LOADING_INTEGRATION']
  };
  
  const requiredFeatures = dependencies[featureName];
  if (requiredFeatures) {
    const missingFeatures = requiredFeatures.filter(feature => !LOADING_FEATURES[feature]);
    if (missingFeatures.length > 0) {
      console.error(`❌ [LoadingFeatures] Cannot enable ${featureName}. Missing dependencies: ${missingFeatures.join(', ')}`);
      return false;
    }
  }
  
  console.log(`🔧 [LoadingFeatures] ${featureName} can be safely enabled`);
  return true;
};

// Environment variable documentation
export const getEnvironmentVariableDocumentation = (): Record<string, string> => {
  return {
    'VITE_NEW_LOADING_SYSTEM': 'Enable the new loading state management system (default: false)',
    'VITE_LOADING_DEBUG': 'Enable loading debug panel in production (default: false)',
    'VITE_LOADING_DEBUG_LOGS': 'Enable debug logging in production (default: false)',
    'VITE_IDLE_DETECTION': 'Enable idle state detection (default: true)',
    'VITE_IDLE_WARNING': 'Enable idle state warnings (default: true)',
    'VITE_LOADING_METRICS': 'Enable performance metrics tracking (default: dev only)',
    'VITE_SKELETON_LOADERS': 'Enable skeleton loading components (default: false)',
    'VITE_ROUTER_LOADING_INTEGRATION': 'Enable router guard integration (default: false)',
    'VITE_DATA_LOADER_ENHANCEMENT': 'Enable enhanced data loaders (default: false)'
  };
};

// Initialize and validate on module load
// NOTE: Removed auto-initialization since we migrated to new project context system
// validateLoadingFeatures() can still be called manually when needed
// if (import.meta.env.DEV) {
//   validateLoadingFeatures();
// }

export default LOADING_FEATURES;